// ----- imports -----
import React, { useState, useRef, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/Preview.css';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';

// ----- all question types -----
const questionTypes = [
  { label: "M.C.Q", value: "mcq" },
  { label: "True-False", value: "true_false" },
  { label: "Fill in the blanks", value: "fill_in_the_blanks" },
  { label: "Very Short Question Answer", value: "very_short_qa" },
  { label: "Short Question Answer", value: "short_qa" },
  { label: "Long Question Answer", value: "long_qa" },
  { label: "Diagram-Based/Image-Based Questions", value: "diagram_image_based" },
  { label: "Matching Types", value: "matching_type" },
];

// ----- main component -----
const Preview = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ----- Paper info state -----
  const [paperName, setPaperName] = useState('');
  const [date, setDate] = useState('');
  const [duration, setDuration] = useState('');
  const [totalMarks, setTotalMarks] = useState('');
  const [typeOfPaper, setTypeOfPaper] = useState("");
  const [marksPerType, setMarksPerType] = useState({});

  // ----- Dropdown & modal -----
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ----- Dashboard & user -----
  const [user, setUser] = useState({ id: null, name: '', email: '', password: '' });
  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // ----- Board / Grade / Subject -----
  const [boardId, setBoardId] = useState(null);
  const [gradeId, setGradeId] = useState(null);
  const [subjectId, setSubjectId] = useState(null);

  // ----- Selected questions -----
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [questionBank, setQuestionBank] = useState({}); // Store full question objects

  // ----- On component load -----
  useEffect(() => {
    if (location.state?.selected_questions) {
      setSelectedQuestions(location.state.selected_questions || {});
    }

    if (location.state?.paperName) setPaperName(location.state.paperName);
    if (location.state?.date) setDate(location.state.date);
    if (location.state?.duration) setDuration(location.state.duration);
    if (location.state?.totalMarks) setTotalMarks(location.state.totalMarks);
    const typePaper = location.state?.typeOfPaper || location.state?.type_of_paper || "";
    setTypeOfPaper(typePaper);

    const bId = location.state?.boardId || localStorage.getItem('board_id');
    const gId = location.state?.gradeId || localStorage.getItem('grade_id');
    const sId = location.state?.subjectId || localStorage.getItem('subject_id');
    setBoardId(bId);
    setGradeId(gId);
    setSubjectId(sId);

    // Fetch user info
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get("https://digital.tifleducation.com/admin/public/api/getDashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.user) {
          setUser({
            id: res.data.user.id,
            name: res.data.user.name || '',
            email: res.data.user.email || '',
            password: '',
          });
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error.response?.data || error);
      }
    };
    fetchUser();
  }, [location.state]);

  // ----- Fetch full question objects -----
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const token = localStorage.getItem("token");
        const allIds = [];
        Object.values(selectedQuestions).forEach(chapter => {
          Object.values(chapter).forEach(arr => allIds.push(...arr));
        });
        const uniqueIds = [...new Set(allIds)];
        if (uniqueIds.length === 0) return;

        const res = await axios.post(
          "https://digital.tifleducation.com/admin/public/api/papers/get-questions-by-ids",
          { ids: uniqueIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const qMap = {};
        res.data.data.forEach(q => { qMap[q.id] = q; });
        setQuestionBank(qMap);
      } catch (error) {
        console.error("Error fetching question details:", error);
      }
    };
    if (Object.keys(selectedQuestions).length > 0) fetchQuestions();
  }, [selectedQuestions]);

  // ----- Profile Save -----
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://digital.tifleducation.com/admin/public/api/update-profile",
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  // ----- Dropdown -----
  const toggleDropdown = () => setShowDropdown(prev => !prev);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  // ----- Get questions by type -----
  const getQuestionsByType = (typeKey) => {
    const allQuestions = [];
    Object.values(selectedQuestions).forEach(chapter => {
      if (chapter[typeKey]) allQuestions.push(...chapter[typeKey]);
    });
    return allQuestions;
  };

  // ----- Save Paper -----
  const handleSavePaper = async (action = "save") => {
    try {
      const token = localStorage.getItem("token");
      const bId = Number(boardId || localStorage.getItem("board_id"));
      const gId = Number(gradeId || localStorage.getItem("grade_id"));
      const sId = Number(subjectId || localStorage.getItem("subject_id"));

      if (!paperName || !date || !duration || !totalMarks || !bId || !gId || !sId) {
        toast.error("Please fill all required fields");
        return;
      }

      // ----- Marks per question type from user input -----
      const finalMarks = {};
      questionTypes.forEach(qt => {
        finalMarks[qt.value] = Number(marksPerType[qt.value] || 0);
      });

      // ----- Counts per question type from selected questions -----
      const finalCounts = {};
      questionTypes.forEach(qt => {
        finalCounts[qt.value] = getQuestionsByType(qt.value).length;
      });

      // ----- Filter out empty question types -----
      const filteredSelectedQuestions = {};
      Object.keys(selectedQuestions).forEach(chapterId => {
        const chapter = selectedQuestions[chapterId];
        const filteredChapter = {};
        Object.keys(chapter).forEach(typeKey => {
          if (chapter[typeKey] && chapter[typeKey].length > 0) {
            filteredChapter[typeKey] = chapter[typeKey];
          }
        });
        if (Object.keys(filteredChapter).length > 0) {
          filteredSelectedQuestions[chapterId] = filteredChapter;
        }
      });

      // ----- Prepare payload -----
      const payload = {
        paper_name: paperName,
        date,
        duration,
        total_marks: Number(totalMarks),
        board: bId,
        grade: gId,
        subject: sId,
        selected_questions: filteredSelectedQuestions,
        action: action,
        paper_type: "regular",
        type_of_paper: typeOfPaper,
        marks: finalMarks,
        ...finalCounts
      };

      const saveRes = await axios.post("https://digital.tifleducation.com/admin/public/api/papers", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Paper saved successfully!");

      // --- clear data ---
localStorage.removeItem("regularPaperForm");
localStorage.removeItem("selectedQuestionsByChapter");
localStorage.removeItem("regularPaperSelection");
localStorage.setItem("regularPaperSaved", "true");
      
      const savedPaperId = saveRes.data.response?.id || location.state?.paperId;
      if (!savedPaperId) {
        toast.error("Failed to get saved paper ID");
        return;
      }

      // Export & redirect
      if (action === "export" || action === "export-answer-key") {
        const exportUrl =
          action === "export"
            ? `https://digital.tifleducation.com/admin/public/papers/export/${savedPaperId}`
            : `https://digital.tifleducation.com/admin/public/papers/export-answer-key/${savedPaperId}`;

        const link = document.createElement("a");
        link.href = exportUrl;
        link.target = "_blank";
        link.click();
        link.remove();

        navigate("/papers/mypapers");
      } else if (action === "save") {
        navigate("/papers/draftpaper");
      }

    } catch (error) {
      console.error("Error saving/exporting paper:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Failed to save/export paper");
    }
  };

  // ----- JSX return -----
  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <main className="main-content">
        <div className="preview-header">
          <div className="preview-welcome-text">
            <button onClick={() => navigate(-1)} className="back-link-prev">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </button>
            <span className="preview-title">Preview Paper</span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className='edituser' onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Paper Info */}
        <div className="dropdown-row">
          <div className="dropdown-group">
            <label>Paper Name <span style={{ color: 'red' }}>*</span></label>
            <input type="text" placeholder='Enter paper Name' className="input-field"
              value={paperName} onChange={(e) => setPaperName(e.target.value)} />
          </div>
          <div className="dropdown-group">
            <label>Type of Paper <span style={{ color: 'red' }}>*</span></label>
            <select value={typeOfPaper} onChange={(e) => setTypeOfPaper(e.target.value)} >
              <option value="">Select Type of Paper</option>
              <option value="Worksheet">Worksheet</option>
              <option value="Assessment">Assessment</option>
            </select>
          </div>
          <div className="dropdown-group">
            <label>Date <span style={{ color: 'red' }}>*</span></label>
            <input type="date" className="input-field" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="dropdown-group">
            <label>Duration <span style={{ color: 'red' }}>*</span></label>
            <input type="text" placeholder='Enter Duration' className="input-field"
              value={duration} onChange={(e) => setDuration(e.target.value)} />
          </div>
          <div className="dropdown-group">
            <label>Total Marks <span style={{ color: 'red' }}>*</span></label>
            <input type="number" placeholder='Enter Total Marks' className="input-field"
              value={totalMarks} onChange={(e) => setTotalMarks(e.target.value)} />
          </div>
        </div>

        {/* Questions Rendering */}
        <div className="preview-section">
          {questionTypes.map((type) => {
            const qIds = getQuestionsByType(type.value);
            if (qIds.length === 0) return null;
            return (
              <div key={type.value}>
                <h3 className="preview-section-title">
                  • {type.label} ({qIds.length})
                  <input type="text" className="marks-input" placeholder="Marks"
                    value={marksPerType[type.value] || ''}
                    onChange={(e) => setMarksPerType(prev => ({ ...prev, [type.value]: e.target.value }))}
                  />
                </h3>
                <div className="preview-box">
                  {qIds.map((qId, idx) => {
                    const q = questionBank[qId];
                    if (!q) return <div key={idx}>Loading question...</div>;

                    let imageUrl = null;
                    let questionText = q.question_title || q.question_text || "No question text";
                    if (q.question_title) {
                      const imgMatch = q.question_title.match(/<img\s+[^>]*src=["']([^"']+)["']/i);
                      if (imgMatch) {
                        imageUrl = imgMatch[1];
                        questionText = questionText.replace(/<img\s+[^>]*>/i, '');
                      }
                    }

                    return (
                      <div key={idx} className="preview-question">
                        <div className="question-header">
                          <input type="checkbox" checked readOnly />
                          <span>{idx + 1}. </span>
                          <span dangerouslySetInnerHTML={{ __html: questionText }} />
                        </div>

                        {/* MCQ Options */}
                        {type.value === "mcq" && q.options && (
                          <div className="preview-options">
                            {q.options.map((opt, i) => {
                              const optionLabel = String.fromCharCode(65 + i);
                              const cleanOpt = opt.replace(/<\/?[^>]+(>|$)/g, "").trim();
                              return <div key={i} className="option-item"><strong>{optionLabel}.</strong> {cleanOpt}</div>;
                            })}
                          </div>
                        )}

                        {/* Diagram/Image */}
                        {type.value === "diagram_image_based" && imageUrl && (
                          <div className="preview-image">
                            <img src={imageUrl} alt="Diagram" style={{ height: '150px', objectFit: 'contain' }} />
                          </div>
                        )}

                        {/* Matching Columns */}
                        {type.value === "matching_type" && q.left_column && q.right_column && (
                          <div className="preview-matching" style={{ display: 'flex', gap: '2rem' }}>
                            <div className="matching-column" style={{ flex: 1 }}><div dangerouslySetInnerHTML={{ __html: q.left_column }} /></div>
                            <div className="matching-column" style={{ flex: 1 }}><div dangerouslySetInnerHTML={{ __html: q.right_column }} /></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Action Buttons */}
          <div className="preview-buttons">
            <button className="edit-btn" onClick={() => navigate("/regularpaper", {
              state: {
                paperId: location.state?.paperId || null,
                boardId,
                gradeId,
                subjectId,
                counts: marksPerType,
                selected_questions: selectedQuestions,
                paperName,
                date,
                duration,
                totalMarks,
                typeOfPaper,
              },
            })}>Edit</button>

            <button className="save-prev-btn" onClick={() => handleSavePaper("save")}>Save</button>
            <button className="export-btn" onClick={() => handleSavePaper("export")}>Export Paper</button>
            <button className="export-btn" onClick={() => handleSavePaper("export-answer-key")}>Export Answer Key</button>
          </div>
        </div>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>
            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={e => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={e => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ----- Modal Styles -----
const modalStyles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", padding: "20px", borderRadius: "10px", width: "400px", boxShadow: "0 5px 15px rgba(0,0,0,0.3)", position: "relative" },
  closeBtn: { position: "absolute", top: "10px", right: "10px", border: "none", background: "transparent", fontSize: "20px", cursor: "pointer" },
  userIcon: { fontSize: "50px", textAlign: "center", marginBottom: "20px", color: "#666" },
  formGroup: { marginBottom: "15px" },
  input: { width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc" },
  saveBtn: { background: "#4caf50", color: "#fff", padding: "10px 20px", border: "none", borderRadius: "5px", cursor: "pointer" }
};

export default Preview;
